import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { createContext, useContext } from 'react';
import { useFeesContext } from 'sonata-platform-components';
import { useDidUpdate, usePrevious } from 'sonata-react-components';
import {
  hasValidMediaType,
  hasValidPricingModel,
  hasValidScheduling,
  hasValidType
} from 'sonata-static-storage/lib/helpers/dynamicBidPrice.helpers';
import { BidModelsMediaTypeEnum, PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { applyMinimumBidPrices } from './pricingModel.helpers';

import type { PropsWithChildren } from 'react';
import type {
  Context,
  ContextData,
  FlagProp,
  Margin,
  MarginProp,
  NumberProp,
  ProviderProps,
  StringProp
} from './pricingModel.types';

const PricingModelContext = createContext<Context | null>(null);

const validateDynamicBidPrice = (contextData: ContextData) => {
  const { pricingModel } = contextData.pricingModelConfig.bidModels[0].bid;
  const { beginDate, endDate, mediaType, optimizationModel, type } = contextData;
  const optimizationModelStatus = !isEmpty(optimizationModel);
  const isValidType = hasValidType(type) && type !== CampaignFamilyEnum.dooh;
  const isValidMediaType = hasValidMediaType(mediaType);
  const isValidScheduling = hasValidScheduling(
    moment.utc(beginDate).format('YYYY-MM-DD') || '',
    moment.utc(endDate).format('YYYY-MM-DD') || ''
  );
  const isValidPricingModel = hasValidPricingModel(pricingModel, optimizationModelStatus);

  return isValidType && isValidMediaType && isValidScheduling && isValidPricingModel;
};

function PricingModelProvider({
  children,
  onChange,
  ...contextData
}: PropsWithChildren<ProviderProps>): React.ReactElement {
  const getSelectedPricingModel = (): PricingModelEnum =>
    Object.values(PricingModelEnum).find(pm => pm === contextData.pricingModelConfig.bidModels[0].bid.pricingModel)!;
  const getDynamicBidPriceValue = (): boolean => contextData.pricingModelConfig.bidModels[0].bid.dynamicBidPrice;

  const { minimumBidPrices } = useFeesContext();
  const { mediaType } = contextData;
  const { cpxOnlyMedia } = contextData.pricingModelConfig;
  const selectedPricingModel = getSelectedPricingModel();
  const previousPricingModel = usePrevious(selectedPricingModel);

  // Only when the pricing model, the marketplace or "media only" change, never while the user types the price
  useDidUpdate(() => {
    const bidModels = applyMinimumBidPrices(
      contextData.pricingModelConfig.bidModels,
      minimumBidPrices,
      previousPricingModel
    );
    if (bidModels) {
      onChange({ ...contextData.pricingModelConfig, bidModels });
    }
  }, [selectedPricingModel, mediaType, cpxOnlyMedia]);

  function setProp(prop: FlagProp, value: boolean): void;
  function setProp(prop: MarginProp, value: Margin): void;
  function setProp(prop: NumberProp, value: number): void;
  function setProp(prop: StringProp, value: string): void;

  function setProp(prop: any, value: any): void {
    onChange({
      ...contextData.pricingModelConfig,
      [prop]: value
    });
  }

  const setPricingModel = (pricingModel: PricingModelEnum) => {
    onChange({
      ...contextData.pricingModelConfig,
      bidModels: contextData.pricingModelConfig.bidModels.map(bm => ({
        ...bm,
        bid: {
          ...bm.bid,
          pricingModel
        }
      }))
    });
  };

  const setDynamicBidPrice = (dynamicBidPrice: boolean, manualSet = true) => {
    onChange({
      ...contextData.pricingModelConfig,
      bidModels: contextData.pricingModelConfig.bidModels.map(bm => ({
        ...bm,
        bid: {
          ...bm.bid,
          dynamicBidPrice,
          dynamicBidPriceManualSet: manualSet
        }
      }))
    });
  };

  const setBidPrice = (bidPrice: number, bidModelMediaType?: BidModelsMediaTypeEnum) => {
    const newBidModels = contextData.pricingModelConfig.bidModels.map((bM, index) => ({
      ...bM,
      bid: {
        ...bM.bid,
        ...((bidModelMediaType === bM.mediaType || (!bidModelMediaType && index === 0)) && {
          price: ([PricingModelEnum['d-cpm'], PricingModelEnum['f-cpm']] as string[]).includes(bM.bid.pricingModel)
            ? bidPrice / 1000
            : bidPrice
        })
      }
    }));
    onChange({
      ...contextData.pricingModelConfig,
      bidModels: newBidModels
    });
  };

  return (
    <PricingModelContext.Provider
      value={{
        ...contextData,
        dynamicBidPrice: getDynamicBidPriceValue(),
        isValidBusinessLogic: validateDynamicBidPrice(contextData),
        pricingModel: getSelectedPricingModel(),
        setBidPrice,
        setDynamicBidPrice,
        setPricingModel,
        setProp
      }}
    >
      {children}
    </PricingModelContext.Provider>
  );
}

function usePricingModel(): Context {
  const context = useContext(PricingModelContext);
  if (!context) {
    throw new Error('usePricingModel cannot be use it outside the PricingModelProvider component');
  }
  return context;
}

export { PricingModelProvider, usePricingModel };
